/* eslint-disable max-len*/
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Typography, useTheme} from '@mui/material';
import escape from 'lodash/escape';
import {useTranslation} from 'react-i18next';
import {useListWorkflowTemplateMessagesQuery} from 'services/schedulerInternal';

import {LiveViewContainer} from './Style';

import {WF_DEFAULT_LANG} from 'Components/WorkflowsList/utils';
import {populateMentionValues} from 'Utils';
import {CUSTOM_NOTIFICATIONS, WF_ACTION_RECEIVER} from 'Utils/consts';
import {global as Bus} from 'Utils/EventBus';
import {fontFaceSetIteratorToArray} from 'Utils/fontUtils';
import {useSenderName, SMS_SENDER_SUFFIX} from 'Utils/hooks';
import zoomPalette from 'zoomPalette';

const W3C_DEFAULT_BODY_MARGIN_IN_PX = 8;

const applyParentFontsToIframe = (iframeRef, customFont) => {
  // Find and load the custom FontFace object of the parent doc into the iframe content as well
  const fontFaceObjects = fontFaceSetIteratorToArray(document.fonts.values()).filter((fontFace) => fontFace.family === customFont);
  // Get the current iframe ref if existent
  const iframeFontSet = iframeRef.current?.contentWindow?.document?.fonts;
  if (iframeFontSet) {
    fontFaceObjects.forEach((fontFaceObject) => {
      iframeFontSet.add(fontFaceObject);
    });
  } else {
    console.debug('iframe not initialized yet');
  }
};

/**
 * Template Preview Component
 * @param {Object} props
 * @return {ReactElement}
 */
export function TemplatePreview(props) {
  const {
    body,
    bodyMargin = W3C_DEFAULT_BODY_MARGIN_IN_PX,
    color,
    customFont,
    defaultHeight,
    fontSize,
    hideBackground,
    isBig,
    isSMS,
    locale=WF_DEFAULT_LANG,
    maxHeight,
    noMargin,
    noMarginInlineEnd,
    receiver,
    seamless,
    subject,
  } = props;
  const {t} = useTranslation();
  const theme = useTheme();
  const {currentSenderName} = useSenderName();
  const containerRef = React.createRef();
  const iframeRef = useRef();
  const [height, setHeight] = useState(defaultHeight || '0px');
  const [showMore, setShowMore] = useState(false);

  const onLoad = useCallback(() => {
    const documentHeight = defaultHeight ||
      iframeRef.current.contentWindow.document.body.scrollHeight +
      bodyMargin * 2 + // Taking top and bottom margins into account
      (subject ? 8 : 0); // oddly, in the iframe, <html> is 8px taller than <body> when there is subject

    if (customFont) {
      applyParentFontsToIframe(iframeRef, customFont);
      iframeRef.current.contentWindow.document.fonts.load(`14px '${customFont}'`);
    }
    setHeight(`${documentHeight}px`);
  }, [customFont, defaultHeight, subject, bodyMargin]);

  useEffect(() => {
    if (customFont) {
      const fontLoader = setTimeout(() => {
        const fontFaceObjects = fontFaceSetIteratorToArray(document.fonts.values()).filter((fontFace) => fontFace.family === customFont);
        // Get the current iframe ref if existent
        const iframeFonts = iframeRef.current?.contentWindow?.document?.fonts;
        if (customFont && iframeFonts) {
          if (!iframeFonts.check(`14px '${customFont}'`)) {
            fontFaceObjects.forEach((fontFaceObject) => {
              iframeRef.current.contentWindow.document.fonts.add(fontFaceObject);
            });
            iframeRef.current.contentWindow.document.fonts.load(`14px '${customFont}'`).then(() => {
              iframeRef.current.contentWindow.document.location.reload();
            }, 200);
          } else {
            iframeRef.current.contentWindow.document.location.reload();
            clearTimeout(fontLoader);
          }
        }
      }, 200);
      return () => clearTimeout(fontLoader);
    }
  }, [customFont]);

  useEffect(() => {
    if (customFont) {
      const fontLoadHandler = () => {
        applyParentFontsToIframe(iframeRef, customFont);
      };
      Bus.on(CUSTOM_NOTIFICATIONS.onCustomFontLoaded, fontLoadHandler);
      return () => {
        Bus.off(CUSTOM_NOTIFICATIONS.onCustomFontLoaded, fontLoadHandler);
      };
    }
  }, [customFont]);

  const templateMessagesApi = useListWorkflowTemplateMessagesQuery(locale, {skip: subject===undefined && !isSMS}, true);
  const localizedVarList = useMemo(() => {
    const apiVars = templateMessagesApi.currentData?.variables ?? [];
    const varList = {
      ...apiVars.reduce((accumVal, currentValue) => {
        accumVal[currentValue.variable.substring(2, currentValue.variable.length-2)] = currentValue.value;
        return accumVal;
      }, {}),
      // ...WF_PREVIEW_LIST,
      // ...t('previewVariableList', {returnObjects: true}),
    };
    return varList;
  }, [templateMessagesApi.currentData?.variables]);

  const {populatedBody, populatedSubject} = useMemo(() => {
    const previewVariableTitles = {
      event_location: t('apptForm.location', {lng: locale}),
      questions_and_answers: t((receiver === WF_ACTION_RECEIVER.BOOKER)? 'notifForm.yourAnswers': 'routing.questions', {lng: locale}),
    };
    const populatedSubject = escape(populateMentionValues(subject, localizedVarList));
    const populatedBody = isSMS ?
      escape(populateMentionValues(body, localizedVarList)) :
      populateMentionValues(body, localizedVarList, true, previewVariableTitles);
    return {populatedSubject, populatedBody};
  }, [body, isSMS, locale, localizedVarList, receiver, subject, t]);

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta http-equiv="X-UA-Compatible" content="ie=edge" />
        <meta
          http-equiv="Content-Security-Policy"
          content="default-src 'self'; connect-src *; object-src 'none'; img-src * data: zmdf:; font-src 'self'; media-src * data: zmdf:; style-src 'self' 'unsafe-eval' 'unsafe-inline'; script-src 'self' 'unsafe-eval' 'unsafe-inline' http://localhost:*; child-src * mailto: tel: callto: zoomphonecall: zoomus: data: zmdf:;"
        />
        <style>
          .non-collapsing-margins {
            padding-top: 0.05px;
            padding-bottom: 0.05px;
          }

          ${maxHeight ?
          `
            Body {
              overflow: hidden;

              /* Hide scrollbar for IE, Edge and Firefox */
              -ms-overflow-style: none;  /* IE and Edge */
              scrollbar-width: none;  /* Firefox */
            }
            /* Hide scrollbar for old Firefox */
            Body::-moz-scrollbar {
              display: none;
            }
            /* Hide scrollbar for Chrome, Safari and Opera */
            Body::-webkit-scrollbar {
              display: none;
            }

            div#bodyPreview {
              max-width: 100%;
              word-wrap: break-word;
            }
          ` :
          ''}
          ${seamless ?
            `
              /* Hide scrollbar for old Firefox */
              Body::-moz-scrollbar {
                display: none;
              }
              /* Hide scrollbar for Chrome, Safari and Opera */
              Body::-webkit-scrollbar {
                display: none;
              }
  
              div#bodyPreview {
                max-width: 100%;
                word-wrap: break-word;
              }
            ` :
            ''}
          div#bodyPreview > * {
            ${noMargin ? 'margin-block-start: 0;' : ''}
          }
          p, ol {
            margin-block-start: 0;
            margin-block-end: ${noMarginInlineEnd ? '0' : '1em'};
          }
          ul {
            padding-inline-start: 20px;
          }
          p:empty::after {
            content: "\xA0";
          }
          a {
            color: ${theme.palette.button.default}
          }
        </style>
        <base target="_blank"> 
        <title></title>
      </head>
      <body
        id="frameBody" class="non-collapsing-margins"
        style="font-family: ${theme.typography.fontFamily}; ${noMargin ? 'margin: 0;' : ''} ${(maxHeight && !showMore) ? 'overflow: hidden;' : ''} ${(color) ? `color: ${color};` : ''} ${(fontSize) ? `font-size: ${parseFloat(fontSize)}px;` : ''}"
      >
        <div id="subjectPreview" class="non-collapsing-margins" style="font-weight: 700; font-size: 16px;">
          ${populatedSubject}
        </div>
        <div id="bodyPreview" class="non-collapsing-margins" ${populatedSubject && 'style="margin-top: 30px;"'}>
          ${populatedBody}
          ${isSMS? ` - ${receiver === WF_ACTION_RECEIVER.BOOKER? currentSenderName: SMS_SENDER_SUFFIX}`: ''}
        </div>
      </body>
    </html>
  `;

  const [hasOverflow, setHasOverflow] = React.useState(undefined);
  React.useEffect(() => {
    if (containerRef?.current) {
      // Temporary hack to fix an edge case bug:
      // When the description content height barely exceeds the height limit, only the bottom margin will be overflowing and hidden.
      // But all the actual text content is shown normally, so when users click the show more button, nothing more is shown, which could be confusing.
      setHasOverflow(containerRef.current.scrollHeight - containerRef.current.clientHeight > 12);
      // setHasOverflow(containerRef.current.scrollHeight > containerRef.current.clientHeight);
    }
  }, [containerRef]);

  return (<>
    <LiveViewContainer
      ref={containerRef}
      isBig={isBig}
      hideBackground={hideBackground}
      seamless={seamless}
      sx={{
        ...maxHeight ?
          showMore ?
            {
              maxHeight: 'unset',
              overflow: 'auto',
            } :
            {
              maxHeight,
              overflow: 'hidden',
            } :
          undefined,
        ...noMargin ? {
          margin: 0,
          padding: 0,
          borderRadius: 0,
        } : undefined,
      }}
    >
      <iframe
        ref={iframeRef}
        onLoad={onLoad}
        title="previewFrame"
        width="100%"
        height={height}
        srcDoc={htmlContent}
        style={{
          borderStyle: 'none',
        }}
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      />
    </LiveViewContainer>
    {!showMore && hasOverflow && <Typography
      onClick={() => setShowMore(true)}
      style={{
        fontSize: '14px',
        color: zoomPalette.blue[60],
        margin: '8px 0px',
        width: 'fit-content',
        cursor: 'pointer',
      }}
    >
      {t('booking.showMoreDescription')}
    </Typography>}
    {showMore && <Typography
      onClick={() => setShowMore(false)}
      style={{
        fontSize: '14px',
        color: zoomPalette.blue[60],
        margin: '8px 0px',
        width: 'fit-content',
        cursor: 'pointer',
      }}
    >
      {t('booking.showLessDescription')}
    </Typography>}
  </>);
}
